import { useTranslation } from 'react-i18next'
import { Info, KeyRound, LogOut } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useLearnerProfile } from '@/hooks/learner/useLearnerProfile'
import { useTodayAttendance } from '@/hooks/learner/useTodayAttendance'
import { toTFn } from '@/utils/i18n'
import { UserInitialAvatar } from '@/components/learner/ui/UserInitialAvatar'
import { LearnerAttendanceStatusBadge } from './LearnerAttendanceStatusBadge'
import { formatGrade } from './lib/formatters'

const CARD_CLASS = 'rounded-2xl bg-lc-bg-primary p-6 shadow-lc-v3-card'

const HEADING_ID = 'profile-card-heading'

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-6">
      <span className="w-14 shrink-0 text-lc-v3-body-1 font-medium text-lc-v3-gray-600">
        {label}
      </span>
      <span className="min-w-0 flex-1 truncate text-lc-v3-body-1 font-medium text-lc-v3-gray-950">
        {value}
      </span>
    </div>
  )
}

function SkeletonRow() {
  return <div className="h-5 w-48 rounded bg-lc-gray-200 animate-pulse my-1.5" />
}

interface ProfileCardProps {
  onPasswordChange: () => void
  onLogout: () => void
}

export function ProfileCard({ onPasswordChange, onLogout }: ProfileCardProps) {
  const { t } = useTranslation('learner')
  const userId = useAuthStore((state) => state.user?.id)
  const { data, isLoading, isError, refetch } = useLearnerProfile()
  const todayAttendance = useTodayAttendance()

  const tFn = toTFn(t)

  return (
    <section className={CARD_CLASS} aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="sr-only">
        {t('profile.section.profile')}
      </h2>
      {isLoading && (
        <div className="flex gap-12 px-6 py-1">
          <div className="size-20 rounded-full bg-lc-v3-gray-200 animate-pulse" />
          <div className="flex-1 space-y-2.5">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        </div>
      )}
      {isError && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-lc-v3-body-2 text-lc-text-secondary">{t('profile.profile.error')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-full bg-lc-v3-gray-100 px-4 py-2 text-lc-v3-caption-1 font-semibold text-lc-text-secondary"
          >
            {t('profile.profile.retry')}
          </button>
        </div>
      )}
      {data && (
        <>
          <div className="flex flex-col gap-6 px-0 md:flex-row md:items-center md:gap-12 md:px-6">
            <UserInitialAvatar
              name={data.name}
              seed={userId}
              className="size-20 text-[30px] leading-[1.5]"
            />
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
              <div className="flex flex-col gap-4">
                <FieldRow label={t('profile.profile.field.name')} value={data.name} />
                <FieldRow label={t('profile.profile.field.loginId')} value={data.loginId} />
                <FieldRow label={t('profile.profile.field.birthDate')} value={data.birthDate} />
              </div>
              <div className="flex flex-col gap-4">
                <FieldRow
                  label={t('profile.profile.field.school')}
                  value={data.schoolName ?? t('profile.profile.field.schoolEmpty')}
                />
                <FieldRow label={t('profile.profile.field.grade')} value={formatGrade(data.grade, tFn)} />
              </div>
            </div>
            <div className="self-start md:self-stretch">
              <LearnerAttendanceStatusBadge
                status={todayAttendance.data?.attendanceStatus ?? null}
              />
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-4 px-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="inline-flex items-center gap-2 text-lc-v3-caption-1 font-semibold text-lc-text-tertiary">
              <Info className="size-4 shrink-0" aria-hidden="true" />
              {t('profile.profile.changeRequest')}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={onPasswordChange}
                aria-haspopup="dialog"
                className="inline-flex h-9 items-center gap-2 rounded-full border border-lc-v3-gray-300 bg-lc-v3-gray-50 px-3.5 text-lc-v3-body-2 font-bold text-lc-text-secondary hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
              >
                <KeyRound className="size-[18px]" aria-hidden="true" />
                {t('profile.section.password')}
              </button>
              <button
                type="button"
                onClick={onLogout}
                aria-haspopup="dialog"
                className="inline-flex h-9 items-center gap-2 rounded-full border border-lc-error-100 bg-lc-error-50 px-3.5 text-lc-v3-body-2 font-bold text-lc-text-error hover:bg-lc-error-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-error-500"
              >
                <LogOut className="size-[18px]" aria-hidden="true" />
                {t('auth.logout.button')}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
